import { ChangeEventHandler } from "react";
import {
  Input,
  InputGroup,
  InputLeftAddon,
  InputProps,
  forwardRef,
} from "@chakra-ui/react";

function splitUrl(
  url: string,
): [string | undefined, string, string | undefined];
function splitUrl(
  url: string | null | undefined,
): [string | undefined, string | undefined, string | undefined];
function splitUrl(
  url: string | null | undefined,
): [string | undefined, string | undefined, string | undefined] {
  const matches = url?.trim()?.match(/^(https?:\/\/)?(.*?)(\/.*)?$/);
  if (!matches) return [undefined, undefined, undefined];
  return [matches[1], matches[2], matches[3]];
}

export function normalizeUrlInputValue(
  value: string | null | undefined,
  allowPath?: boolean,
) {
  const [protocol, hostname, path] = splitUrl(value);
  if (!hostname) return null;

  const origin = `${protocol || "https://"}${hostname}`.toLowerCase();
  return allowPath ? `${origin}${path ?? ""}` : `${origin}/`;
}

type Props = Omit<InputProps, "value" | "onChange"> & {
  onChange?: (value: string | null) => void;
  value?: string | null;
  allowPath?: boolean;
};

const UrlInput = forwardRef<Props, "div">(
  (
    {
      onChange,
      value,
      allowPath,
      autoComplete = "url",
      size,
      variant,
      ...rest
    },
    ref,
  ) => {
    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      onChange?.(normalizeUrlInputValue(e.target.value, allowPath));
    };

    const [protocol, website, path] = splitUrl(value);
    const lcProtocol = protocol?.toLowerCase();
    const lcWebsite = website?.toLowerCase();

    return (
      <InputGroup ref={ref} size={size} variant={variant}>
        <InputLeftAddon>{lcProtocol || "https://"}</InputLeftAddon>
        <Input
          value={
            (allowPath ? `${lcWebsite || ""}${path || ""}` : lcWebsite) || ""
          }
          onChange={handleChange}
          type="url"
          autoComplete={autoComplete}
          data-1p-ignore={autoComplete === "off" || undefined}
          data-lpignore={autoComplete === "off" || undefined}
          {...rest}
        />
      </InputGroup>
    );
  },
);

export default UrlInput;
